import type { AxiosInstance, AxiosError } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { CreateContentReportRequest } from '../types/moderation/CreateContentReportRequest';
import type { CreateUserBlockRequest } from '../types/moderation/CreateUserBlockRequest';
import { apiClient } from '../client/client';

/**
 * User moderation: content reports and block list (POST/GET/DELETE api/blocks, POST api/reports).
 */
export class ModerationService {
  private static readonly REPORTS = '/api/reports';
  private static readonly BLOCKS = '/api/blocks';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async createReport(
    payload: CreateContentReportRequest
  ): Promise<ServiceResultT<string | null>> {
    try {
      const { data } = await this.client.post<ServiceResultT<string | null>>(
        ModerationService.REPORTS,
        payload
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<string | null>>(e);
    }
  }

  async blockUser(payload: CreateUserBlockRequest): Promise<ServiceResult> {
    try {
      const { data } = await this.client.post<ServiceResult>(ModerationService.BLOCKS, payload);
      return data;
    } catch (e) {
      return this.mapError<ServiceResult>(e);
    }
  }

  async getBlockedUserIds(): Promise<ServiceResultT<string[] | null>> {
    try {
      const { data } = await this.client.get<ServiceResultT<string[] | null>>(
        ModerationService.BLOCKS
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<string[] | null>>(e);
    }
  }

  async unblockUser(blockedUserId: string): Promise<ServiceResult> {
    try {
      const { data } = await this.client.delete<ServiceResult>(
        `${ModerationService.BLOCKS}/${blockedUserId}`
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResult>(e);
    }
  }

  private mapError<T extends ServiceResult | ServiceResultT<unknown>>(e: unknown): T {
    const err = e as AxiosError<T>;
    const body = err.response?.data;
    const status = err.response?.status ?? 0;
    if (body && typeof body === 'object' && 'isSuccess' in body) {
      return {
        ...body,
        statusCode: (body as { statusCode?: number }).statusCode ?? status,
      } as T;
    }
    return {
      isSuccess: false,
      statusCode: status,
      message: err.message ?? 'İstek başarısız.',
    } as T;
  }
}

export const moderationService = new ModerationService();
